/**
 * Top Sparks report page.
 */

import { useTopSparks } from '@/hooks/useAnalytics';
import { Card } from '@/components/ui/card';
import { DataTable } from '@/components/tables/data-table';
import { LoadingSpinner } from '@/components/ui/loading';
import { TrendingUp } from 'lucide-react';
import type { TopSpark } from '@/api/schemas';
import type { Column } from '@/components/tables/data-table';
import { Link } from 'react-router-dom';
import './top-sparks.scss';

export function TopSparksReport() {
  const { data, isLoading, error } = useTopSparks({ limit: 50 });

  const columns: Column<TopSpark>[] = [
    {
      key: 'ref_host',
      header: 'Referrer Host',
      render: (spark) => <span className="font-medium">{spark.ref_host}</span>,
    },
    {
      key: 'spark_score',
      header: 'Spark Score',
      render: (spark) => (
        <span className="font-semibold" style={{ color: 'var(--of-flow-blue)' }}>{spark.spark_score.toFixed(2)}</span>
      ),
    },
    {
      key: 'clicks',
      header: 'Clicks',
      render: (spark) => spark.clicks.toLocaleString(),
    },
    {
      key: 'placement_id',
      header: 'Placement',
      render: (spark) => (
        <Link
          to={`/placements/${spark.placement_id}`}
          className="top-sparks-report__link"
        >
          View Details
        </Link>
      ),
    },
  ];

  return (
    <div className="top-sparks-report">
      <div className="top-sparks-report__header">
        <h1 className="h1">Top Sparks</h1>
        <p className="subtitle">
          Discover the most viral sources driving traffic to your links
        </p>
      </div>

      <Card>
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : error ? (
          <div className="top-sparks-report__empty">
            <TrendingUp />
            <p>Error loading sparks data: {error instanceof Error ? error.message : 'Unknown error'}</p>
          </div>
        ) : data && data.sparks && data.sparks.length > 0 ? (
          <DataTable columns={columns} data={data.sparks} />
        ) : (
          <div className="top-sparks-report__empty">
            <TrendingUp />
            <p>No sparks data available</p>
          </div>
        )}
      </Card>
    </div>
  );
}

